import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { X, Loader2, Upload, RefreshCw } from "lucide-react";
import { Product, Category, Brand, Unit } from "@apis/index";
import theme from "@utils/theme";
import { toast } from "react-toastify";
import Button from "@components/button";

interface CreateProductModalProps {
  storeId: number;
  moduleId: number;
  hideModal: () => void;
  onProductCreated: (product: any) => void;
}

const CreateProductModal = ({
  storeId,
  moduleId,
  hideModal,
  onProductCreated,
}: CreateProductModalProps) => {
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  // Data for dropdowns
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);

  const [productData, setProductData] = useState({
    name: "",
    price: "",
    sku: "",
    unit: "",
    categoryId: "",
    subCategoryId: "",
    brandId: "",
    showInApp: true,
  });
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      if (!moduleId) return;
      try {
        setDataLoading(true);
        const [catRes, brandRes, unitRes] = await Promise.all([
          Category.list({ params: { parentId: 0, moduleId, limit: 1000 } }),
          Brand.list({ params: { moduleId, limit: 1000 } }),
          Unit.list({ params: { moduleId, limit: 1000 } }),
        ]);

        setCategories(catRes?.items || []);
        setBrands(brandRes?.items || []);
        setUnits(unitRes?.items || []);
      } catch (err) {
        console.error("Failed to fetch product setup data", err);
      } finally {
        setDataLoading(false);
      }
    };
    fetchData();
  }, [moduleId]);

  // Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubs = async () => {
      if (!productData.categoryId) {
        setSubCategories([]);
        return;
      }
      try {
        const res = await Category.list({
          params: { parentId: productData.categoryId, moduleId, limit: 1000 },
        });
        setSubCategories(res?.items || []);
      } catch (err) {
        console.error("Failed to fetch subcategories", err);
      }
    };
    fetchSubs();
  }, [productData.categoryId, moduleId]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const generateSKU = () => {
    const random = Math.floor(Math.random() * 9000) + 1000;
    const prefix = productData.name
      ? productData.name.substring(0, 3).toUpperCase()
      : "PROD";
    setProductData({ ...productData, sku: `${prefix}-${random}` });
  };

  const handleCreate = async () => {
    if (!productData.name.trim()) {
      toast.error("Product name is required");
      return;
    }
    if (!productData.price || Number(productData.price) <= 0) {
      toast.error("Valid price is required");
      return;
    }
    if (!productData.sku.trim()) {
      toast.error("SKU is required");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("name", productData.name.trim());
      formData.append("price", productData.price);
      formData.append("sku", productData.sku.trim());
      formData.append("storeId", String(storeId));
      formData.append("showInApp", String(productData.showInApp));
      formData.append("isActive", "true");
      if (productData.categoryId)
        formData.append("categoryId", productData.categoryId);
      if (productData.subCategoryId) {
        formData.append("subCategoryIds[0]", productData.subCategoryId);
      }
      if (productData.brandId) formData.append("brandId", productData.brandId);

      if (image) {
        formData.append("images", image);
      }

      const res = await Product.create({ data: formData });

      if (res?.id) {
        toast.success("Product created successfully");
        onProductCreated(res);
        hideModal();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to create product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay onClick={(e) => e.stopPropagation()}>
      <ModalContainer>
        <Header>
          <Title>Create New Product</Title>
          <CloseButton onClick={hideModal}>
            <X size={20} />
          </CloseButton>
        </Header>

        <Content>
          <div
            style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}
          >
            <ImageUploadSection>
              <Label>Product Image (Optional)</Label>
              <ImagePlaceholder
                onClick={() => fileInputRef.current?.click()}
                hasImage={!!imagePreview}
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" />
                ) : (
                  <UploadContent>
                    <Upload size={24} />
                    <span>Click to upload image</span>
                  </UploadContent>
                )}
              </ImagePlaceholder>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
            </ImageUploadSection>

            <FormSection style={{ flex: 1 }}>
              <FormField>
                <Label>Product Name *</Label>
                <Input
                  type="text"
                  placeholder="Enter Product Name"
                  value={productData.name}
                  onChange={(e) =>
                    setProductData({ ...productData, name: e.target.value })
                  }
                />
              </FormField>

              <FormRow>
                <FormField>
                  <Label>Price (NPR) *</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={productData.price}
                    onChange={(e) =>
                      setProductData({ ...productData, price: e.target.value })
                    }
                  />
                </FormField>
                <FormField>
                  <Label>SKU / Model *</Label>
                  <InputGroup>
                    <Input
                      type="text"
                      placeholder="Enter SKU"
                      value={productData.sku}
                      onChange={(e) =>
                        setProductData({ ...productData, sku: e.target.value })
                      }
                    />
                    <GenerateBtn
                      onClick={generateSKU}
                      type="button"
                      title="Generate SKU"
                    >
                      <RefreshCw size={14} />
                    </GenerateBtn>
                  </InputGroup>
                </FormField>
              </FormRow>
            </FormSection>
          </div>

          <Divider />

          <FormSection>
            <SectionTitle>Store And Category Info</SectionTitle>
            <FormGrid>
              <FormField>
                <Label>Unit</Label>
                <Select
                  value={productData.unit}
                  onChange={(e) =>
                    setProductData({ ...productData, unit: e.target.value })
                  }
                  disabled={dataLoading}
                >
                  <option value="">--Select Unit--</option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.name}>
                      {unit.name}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField>
                <Label>Show in App</Label>
                <div style={{ display: 'flex', alignItems: 'center', height: '42px', gap: '8px' }}>
                  <ToggleInput
                    type="checkbox"
                    id="showInApp"
                    checked={productData.showInApp}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        showInApp: e.target.checked,
                      })
                    }
                  />
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>Visible to customers</span>
                </div>
              </FormField>
            </FormGrid>
          </FormSection>
        </Content>

        <Footer>
          <Button title="Cancel" onClick={hideModal} width={100} />
          <Button
            title={loading ? "Creating..." : "Create & Add to Order"}
            onClick={handleCreate}
            disabled={loading || dataLoading}
            width={180}
          />
        </Footer>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default CreateProductModal;

/* Styles */
const ModalOverlay = styled.div`
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
  background: #f9fafb;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  transition: color 0.2s;
  &:hover {
    color: #111827;
  }
`;

const Content = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-height: 70vh;
  overflow-y: auto;
`;

const SectionTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 15px;
  font-weight: 600;
  color: #374151;
`;

const ImageUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
`;

const ImagePlaceholder = styled.div<{ hasImage: boolean }>`
  width: 140px;
  height: 140px;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  overflow: hidden;
  background: #f9fafb;
  transition: all 0.2s;

  &:hover {
    border-color: ${theme.colors.primary};
    background: #f3f4f6;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UploadContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #9ca3af;
  text-align: center;
  padding: 10px;
  span {
    font-size: 11px;
    font-weight: 500;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const FormRow = styled.div`
  display: flex;
  gap: 16px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  height: 42px;
  transition: border-color 0.2s;
  &:focus {
    border-color: ${theme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  background: #fff;
  cursor: pointer;
  height: 42px;
  &:focus {
    border-color: ${theme.colors.primary};
  }
  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
  }
`;

const GenerateBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  color: #374151;
  cursor: pointer;
  height: 42px;
  transition: all 0.2s;
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

const ToggleInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${theme.colors.primary};
  margin: 0;
`;

const Divider = styled.div`
  height: 1px;
  background: #eee;
  width: 100%;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  background: #f9fafb;
  border-top: 1px solid #eee;
`;
